import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Modal } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import { Check, Close, Error } from '@/components/Icon';
import ReviewSummaryContent from '@/components/Pbl/VolunteerRegistration/ReviewSummaryContent';
import { initialVolunteerForm } from '@/components/Pbl/VolunteerRegistration/constants';
import type { VolunteerFormState } from '@/components/Pbl/VolunteerRegistration/types';
import { theme } from '@/theme/Theme';
import type {
  PblAppointmentOption,
  PblAppointmentProposal,
  PblAppointmentProposalUpdatePayload,
  PblDocument,
  PblLawyerCvSummary,
  PblMeeting,
  PblMeetingUpdatePayload,
} from '@/types/pbl';
import {
  getPblMeetingEndDateTime,
  getPblMeetingEditModalNotice,
  getPblMeetingEditModalTitle,
  isPblMeetingWithinSameDay,
} from '@/utils/pblMeeting';
import { translatePblDisplayList, translatePblDisplayValue } from '@/utils/pblDisplayTranslation';

import {
  formatDateTime,
  formatFileSize,
  getDocumentFileName,
  getDocumentFilePath,
  getDocumentFileType,
  getDocumentMetadataText,
} from './helpers';
import {
  DetailGrid,
  DetailLabel,
  DetailValue,
  ModalActions,
  ModalCloseButton,
  ModalFieldStack,
  ModalHeader,
  ModalInput,
  ModalLabel,
  ModalNotice,
  ModalSelect,
  ModalSurface,
  ModalTextarea,
  ModalTitle,
  ReviewSummaryModalCloseButton,
  ReviewSummaryModalScroll,
  ReviewSummaryModalSurface,
  ResultIcon,
} from './styles';

type BaseModalProps = {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
};

const closeModal = (setShowModal: (show: boolean) => void) => () => setShowModal(false);

const toDateTimeLocalValue = (value?: string | null) => {
  if (!value) return '';

  const normalizedValue = value.replace(' ', 'T');
  const match = normalizedValue.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);

  return match ? `${match[1]}T${match[2]}` : '';
};

const fromDateTimeLocalValue = (value: string) => (value ? `${value}:00` : value);

export type CancelCaseInput = {
  reason: string;
};

export type ReassignLawyerInput = {
  reason: string;
};

export type LawyerExitRequestInput = {
  reasonCode: 'scheduling_unavailable' | 'outside_expertise' | 'capacity_or_conflict' | 'other';
  reason: string;
};

const getRecordSummary = (document?: PblDocument | null) => {
  if (!document) return '-';

  const latestRecord = document?.blockchain_records?.[0];

  if (latestRecord?.hash) return latestRecord.hash;

  return getDocumentMetadataText(document, ['blockchain_hash', 'hash']) || '-';
};

export const DocumentDetailModal = ({
  showModal,
  setShowModal,
  document,
  isLoading,
  onDownloadDocument,
}: BaseModalProps & {
  document: PblDocument | null;
  isLoading: boolean;
  onDownloadDocument: (document: PblDocument) => void;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const filePath = document ? getDocumentFilePath(document) : undefined;
  const detailRows = useMemo(
    () =>
      document
        ? [
            [t('pbl.caseDetailDocuments.modals.documentDetail.fields.fileName'), getDocumentFileName(document)],
            [
              t('pbl.caseDetailDocuments.modals.documentDetail.fields.itemType'),
              getDocumentMetadataText(document, ['item_type', 'doc_type']) || getDocumentFileType(document),
            ],
            [t('pbl.caseDetailDocuments.modals.documentDetail.fields.documentType'), document.document_type],
            [t('pbl.caseDetailDocuments.modals.documentDetail.fields.fileSize'), formatFileSize(document.storage_file?.size)],
            [t('pbl.caseDetailDocuments.modals.documentDetail.fields.createdAt'), formatDateTime(document.created_at)]
          ]
        : [],
    [document, t]
  );

  return (
    <Modal open={showModal} onClose={closeModal(setShowModal)} disableEnforceFocus>
      <ModalSurface>
        <ModalHeader>
          <ModalTitle>{t('pbl.caseDetailDocuments.modals.documentDetail.title')}</ModalTitle>
          <ModalCloseButton
            type="button"
            onClick={closeModal(setShowModal)}
            aria-label={t('pbl.caseDetailDocuments.modals.documentDetail.closeAriaLabel')}
          >
            <Close.CloseSVG />
          </ModalCloseButton>
        </ModalHeader>
        {isLoading ? (
          <ModalNotice>{t('pbl.caseDetailDocuments.modals.documentDetail.loading')}</ModalNotice>
        ) : document ? (
          <>
            <ModalNotice>{t('pbl.caseDetailDocuments.modals.documentDetail.notice')}</ModalNotice>
            <DetailGrid>
              {detailRows.map(([label, value]) => (
                <div key={label} style={{ display: 'contents' }}>
                  <DetailLabel>{label}</DetailLabel>
                  <DetailValue>{value}</DetailValue>
                </div>
              ))}
            </DetailGrid>
            <ModalActions>
              <Button
                type="button"
                size="lg"
                variant="primary"
                rounded={false}
                disabled={!filePath}
                onClick={() => onDownloadDocument(document)}
              >
                {t('pbl.caseDetailDocuments.modals.documentDetail.download')}
              </Button>
              <Button type="button" size="lg" variant="primary-invert" rounded={false} onClick={closeModal(setShowModal)}>
                {t('pbl.caseDetailDocuments.modals.documentDetail.close')}
              </Button>
            </ModalActions>
          </>
        ) : (
          <ModalNotice>{t('pbl.caseDetailDocuments.modals.documentDetail.empty')}</ModalNotice>
        )}
      </ModalSurface>
    </Modal>
  );
};

const joinPublicCvNameParts = (parts: Array<string | null | undefined>, separator: string) =>
  parts.map((value) => value?.trim()).filter(Boolean).join(separator);

const firstPublicCvText = (...values: Array<string | null | undefined>) =>
  values.map((value) => value?.trim()).find(Boolean) || '';

const joinPublicCvList = (values?: string[] | null) => (values && values.length > 0 ? values.join(', ') : '');
const joinPublicCvLabels = (values?: Array<{ label_en?: string | null; label_zh?: string | null; value?: string }> | null) =>
  values && values.length > 0
    ? values.map((item) => item.label_en || item.label_zh || item.value).filter(Boolean).join(', ')
    : '';
const toPublicCvLabelList = (
  values?: Array<{ label_en?: string | null; label_zh?: string | null; value?: string }> | null
) =>
  values
    ? values
        .map((item) => item.label_en || item.label_zh || item.value)
        .filter((value): value is string => Boolean(value))
    : [];
const splitPublicCvList = (value?: string | null) =>
  value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

type CaseDetailTranslation = (key: string, options?: Record<string, unknown>) => string;

const getLawyerCvRows = (cv: PblLawyerCvSummary, t: CaseDetailTranslation) => {
  const languages =
    firstPublicCvText(cv.cv_languages_en, cv.languages_en) ||
    joinPublicCvLabels(cv.language_labels) ||
    joinPublicCvList(cv.language_proficiency);
  const serviceAreas = joinPublicCvLabels(cv.service_area_labels) || joinPublicCvList(cv.service_areas);
  const lawFirmEn = firstPublicCvText(cv.cv_law_firm_en, cv.law_firm_name_en, cv.law_firm_name);
  const lawFirmZh = firstPublicCvText(cv.cv_law_firm_zh, cv.law_firm_name_zh);
  const positionEn = firstPublicCvText(cv.cv_position_en, cv.position_en, cv.position_in_law_firm);
  const positionZh = firstPublicCvText(cv.cv_position_zh, cv.position_zh);
  const admissionYearEn = firstPublicCvText(cv.cv_admission_year_en, cv.admission_year_en);
  const otherQualificationsEn = firstPublicCvText(cv.cv_other_qualifications_en, cv.other_qualifications_en);
  const primaryPracticeAreasEn = firstPublicCvText(cv.cv_primary_practice_areas_en, cv.primary_practice_areas_en);
  const professionalExperienceEn = firstPublicCvText(cv.cv_professional_experience_en, cv.professional_experience_en);
  const proBonoExperienceEn = firstPublicCvText(cv.cv_pro_bono_experience_en, cv.pro_bono_experience_en);

  return [
    [
      t('pbl.caseDetailDocuments.modals.lawyerCv.fields.name'),
      cv.cv_full_name_en ||
        cv.display_name_en ||
        cv.full_name_en ||
        joinPublicCvNameParts([cv.surname_en, cv.given_name_en], ' '),
    ],
    [
      t('pbl.caseDetailDocuments.modals.lawyerCv.fields.chineseName'),
      cv.cv_full_name_zh ||
        cv.display_name_zh ||
        cv.full_name_zh ||
        joinPublicCvNameParts([cv.surname_zh, cv.given_name_zh], ''),
    ],
    [t('pbl.caseDetailDocuments.modals.lawyerCv.fields.lawFirm'), lawFirmEn],
    [t('pbl.caseDetailDocuments.modals.lawyerCv.fields.chineseLawFirm'), lawFirmZh],
    [
      t('pbl.caseDetailDocuments.modals.lawyerCv.fields.position'),
      translatePblDisplayValue(t, positionEn),
    ],
    [t('pbl.caseDetailDocuments.modals.lawyerCv.fields.chinesePosition'), positionZh],
    [t('pbl.caseDetailDocuments.modals.lawyerCv.fields.admissionYear'), admissionYearEn],
    [
      t('pbl.caseDetailDocuments.modals.lawyerCv.fields.otherQualifications'),
      translatePblDisplayList(t, otherQualificationsEn),
    ],
    [t('pbl.caseDetailDocuments.modals.lawyerCv.fields.languages'), translatePblDisplayList(t, languages)],
    [t('pbl.caseDetailDocuments.modals.lawyerCv.fields.serviceAreas'), translatePblDisplayList(t, serviceAreas)],
    [
      t('pbl.caseDetailDocuments.modals.lawyerCv.fields.primaryPracticeAreas'),
      translatePblDisplayList(t, primaryPracticeAreasEn),
    ],
    [t('pbl.caseDetailDocuments.modals.lawyerCv.fields.professionalExperience'), professionalExperienceEn],
    [t('pbl.caseDetailDocuments.modals.lawyerCv.fields.proBonoExperience'), proBonoExperienceEn],
  ].filter(([, value]) => Boolean(value));
};

export const LawyerCvModal = ({
  showModal,
  setShowModal,
  cv,
  isLoading,
}: BaseModalProps & {
  cv: PblLawyerCvSummary | null;
  isLoading: boolean;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const detailRows = cv ? getLawyerCvRows(cv, t) : [];

  return (
    <Modal open={showModal} onClose={closeModal(setShowModal)} disableEnforceFocus>
      <ModalSurface>
        <ModalHeader>
          <ModalTitle>{t('pbl.caseDetailDocuments.modals.lawyerCv.title')}</ModalTitle>
          <ModalCloseButton
            type="button"
            onClick={closeModal(setShowModal)}
            aria-label={t('pbl.caseDetailDocuments.modals.lawyerCv.closeAriaLabel')}
          >
            <Close.CloseSVG />
          </ModalCloseButton>
        </ModalHeader>
        {isLoading ? (
          <ModalNotice>{t('pbl.caseDetailDocuments.modals.lawyerCv.loading')}</ModalNotice>
        ) : detailRows.length > 0 ? (
          <>
            <DetailGrid>
              {detailRows.map(([label, value]) => (
                <div key={label} style={{ display: 'contents' }}>
                  <DetailLabel>{label}</DetailLabel>
                  <DetailValue>{value}</DetailValue>
                </div>
              ))}
            </DetailGrid>
            <ModalActions>
              <Button type="button" size="lg" variant="primary-invert" rounded={false} onClick={closeModal(setShowModal)}>
                {t('pbl.caseDetailDocuments.modals.lawyerCv.close')}
              </Button>
            </ModalActions>
          </>
        ) : (
          <ModalNotice>{t('pbl.caseDetailDocuments.modals.lawyerCv.empty')}</ModalNotice>
        )}
      </ModalSurface>
    </Modal>
  );
};

const getLawyerCvNameEn = (cv: PblLawyerCvSummary) =>
  cv.cv_full_name_en ||
  cv.display_name_en ||
  cv.full_name_en ||
  joinPublicCvNameParts([cv.surname_en, cv.given_name_en], ' ');

const getLawyerCvNameZh = (cv: PblLawyerCvSummary) =>
  cv.cv_full_name_zh ||
  cv.display_name_zh ||
  cv.full_name_zh ||
  joinPublicCvNameParts([cv.surname_zh, cv.given_name_zh], '');

const buildLawyerCvReviewSummary = (
  cv: PblLawyerCvSummary
): { form: VolunteerFormState; serviceAreaLabels: string[] } => {
  const primaryPracticeAreasEn = firstPublicCvText(cv.cv_primary_practice_areas_en, cv.primary_practice_areas_en);
  const serviceAreaLabels =
    toPublicCvLabelList(cv.service_area_labels).length > 0
      ? toPublicCvLabelList(cv.service_area_labels)
      : splitPublicCvList(primaryPracticeAreasEn).length > 0
      ? splitPublicCvList(primaryPracticeAreasEn)
      : cv.service_areas || [];
  const serviceAreaText = serviceAreaLabels.join(', ');
  const nameEn = getLawyerCvNameEn(cv);
  const nameZh = getLawyerCvNameZh(cv);
  const lawFirmEn = firstPublicCvText(cv.cv_law_firm_en, cv.law_firm_name_en, cv.law_firm_name);
  const lawFirmZh = firstPublicCvText(cv.cv_law_firm_zh, cv.law_firm_name_zh);
  const positionEn = firstPublicCvText(cv.cv_position_en, cv.position_en, cv.position_in_law_firm);
  const languages =
    firstPublicCvText(cv.cv_languages_en, cv.languages_en) ||
    joinPublicCvLabels(cv.language_labels) ||
    joinPublicCvList(cv.language_proficiency);

  return {
    form: {
      ...initialVolunteerForm,
      full_name_en: nameEn,
      full_name_zh: nameZh,
      phone: '-',
      email: '-',
      hk_solicitor_admission_year: firstPublicCvText(cv.cv_admission_year_en, cv.admission_year_en),
      law_firm_name: lawFirmEn,
      law_firm_name_zh: lawFirmZh,
      position_in_law_firm: positionEn,
      service_areas: cv.service_areas || [],
      cv_publication_consent: true,
      cv_full_name_en: nameEn,
      cv_full_name_zh: nameZh,
      cv_law_firm_en: lawFirmEn,
      cv_law_firm_zh: lawFirmZh,
      cv_position_en: positionEn,
      cv_position_zh: firstPublicCvText(cv.cv_position_zh, cv.position_zh),
      cv_admission_year_en: firstPublicCvText(cv.cv_admission_year_en, cv.admission_year_en),
      cv_admission_year_zh: firstPublicCvText(cv.cv_admission_year_zh, cv.admission_year_zh),
      cv_other_qualifications_en: firstPublicCvText(cv.cv_other_qualifications_en, cv.other_qualifications_en),
      cv_other_qualifications_zh: firstPublicCvText(cv.cv_other_qualifications_zh, cv.other_qualifications_zh),
      cv_languages_en: languages,
      cv_languages_zh: firstPublicCvText(cv.cv_languages_zh, cv.languages_zh),
      cv_primary_practice_areas_en: primaryPracticeAreasEn || serviceAreaText,
      cv_primary_practice_areas_zh: firstPublicCvText(cv.cv_primary_practice_areas_zh, cv.primary_practice_areas_zh),
      cv_professional_experience_en: firstPublicCvText(
        cv.cv_professional_experience_en,
        cv.professional_experience_en
      ),
      cv_professional_experience_zh: firstPublicCvText(
        cv.cv_professional_experience_zh,
        cv.professional_experience_zh
      ),
      cv_pro_bono_experience_en: firstPublicCvText(cv.cv_pro_bono_experience_en, cv.pro_bono_experience_en),
      cv_pro_bono_experience_zh: firstPublicCvText(cv.cv_pro_bono_experience_zh, cv.pro_bono_experience_zh),
    },
    serviceAreaLabels,
  };
};

export const LawyerReviewSummaryModal = ({
  showModal,
  setShowModal,
  cv,
  isLoading,
}: BaseModalProps & {
  cv: PblLawyerCvSummary | null;
  isLoading: boolean;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const summary = useMemo(() => (cv ? buildLawyerCvReviewSummary(cv) : null), [cv]);

  return (
    <Modal open={showModal} onClose={closeModal(setShowModal)} disableEnforceFocus>
      <ReviewSummaryModalSurface>
        <ReviewSummaryModalCloseButton
          type="button"
          onClick={closeModal(setShowModal)}
          aria-label={t('pbl.caseDetailDocuments.modals.lawyerCv.closeAriaLabel')}
        >
          <Close.CloseSVG />
        </ReviewSummaryModalCloseButton>
        <ReviewSummaryModalScroll>
          {isLoading ? (
            <ModalNotice>{t('pbl.caseDetailDocuments.modals.lawyerCv.loading')}</ModalNotice>
          ) : summary ? (
            <ReviewSummaryContent
              form={summary.form}
              serviceAreaLabels={summary.serviceAreaLabels}
              showHeader={false}
              showEditLinks={false}
              showSubmitNotice={false}
              hideSectionsFromAuthorisation
            />
          ) : (
            <ModalNotice>{t('pbl.caseDetailDocuments.modals.lawyerCv.empty')}</ModalNotice>
          )}
        </ReviewSummaryModalScroll>
      </ReviewSummaryModalSurface>
    </Modal>
  );
};

export const RecordResultModal = ({
  showModal,
  setShowModal,
  success,
  message,
}: BaseModalProps & {
  success: boolean;
  message: string;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();

  return (
  <Modal open={showModal} onClose={closeModal(setShowModal)} disableEnforceFocus>
    <ModalSurface>
      <ModalHeader>
        <ModalTitle>{t('pbl.caseDetailDocuments.modals.blockchainResult.title')}</ModalTitle>
        <ModalCloseButton
          type="button"
          onClick={closeModal(setShowModal)}
          aria-label={t('pbl.caseDetailDocuments.modals.blockchainResult.closeAriaLabel')}
        >
          <Close.CloseSVG />
        </ModalCloseButton>
      </ModalHeader>
      <ResultIcon success={success}>{success ? <Check.CheckSVG /> : <Error.ErrorSVG />}</ResultIcon>
      <Text textAlign="center" color={theme.colors.gray500}>
        {message}
      </Text>
      <ModalActions>
        <Button type="button" size="lg" variant="primary" rounded={false} onClick={closeModal(setShowModal)}>
          {t('pbl.caseDetailDocuments.modals.blockchainResult.close')}
        </Button>
      </ModalActions>
    </ModalSurface>
  </Modal>
  );
};

export const AppointmentProposalEditModal = ({
  showModal,
  setShowModal,
  proposal,
  isSubmitting,
  onSubmit,
  reasonRequired = false,
}: BaseModalProps & {
  proposal: PblAppointmentProposal | null;
  isSubmitting: boolean;
  onSubmit: (payload: PblAppointmentProposalUpdatePayload) => Promise<void>;
  reasonRequired?: boolean;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const [meetingType, setMeetingType] = useState<'video' | 'voice'>('video');
  const [options, setOptions] = useState<Array<{ starts_at: string; note: string }>>([]);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!showModal || !proposal) return;

    setMeetingType(proposal.meeting_type === 'voice' ? 'voice' : 'video');
    setOptions(
      (proposal.options || []).map((option) => ({
        starts_at: toDateTimeLocalValue(option.starts_at),
        note: option.note || '',
      }))
    );
    setReason('');
  }, [proposal, showModal]);

  const canAddOption = options.length < 5;
  const canRemoveOption = options.length > 2;
  const canSubmit =
    !isSubmitting &&
    (!reasonRequired || Boolean(reason.trim())) &&
    options.length >= 2 &&
    options.length <= 5 &&
    options.every((option) => isPblMeetingWithinSameDay(option.starts_at));

  const updateOption = (index: number, field: 'starts_at' | 'note', value: string) => {
    setOptions((currentOptions) =>
      currentOptions.map((option, optionIndex) =>
        optionIndex === index ? { ...option, [field]: value } : option
      )
    );
  };

  const addOption = () => {
    if (!canAddOption) return;
    setOptions((currentOptions) => [...currentOptions, { starts_at: '', note: '' }]);
  };

  const removeOption = (index: number) => {
    if (!canRemoveOption) return;
    setOptions((currentOptions) => currentOptions.filter((_, optionIndex) => optionIndex !== index));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    const payloadOptions: PblAppointmentOption[] = options.map((option) => ({
      starts_at: fromDateTimeLocalValue(option.starts_at),
      ends_at: fromDateTimeLocalValue(getPblMeetingEndDateTime(option.starts_at)!),
      ...(option.note.trim() ? { note: option.note.trim() } : {}),
    }));

    await onSubmit({
      meeting_type: meetingType,
      options: payloadOptions,
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    });
  };

  return (
    <Modal open={showModal} onClose={closeModal(setShowModal)} disableEnforceFocus>
      <ModalSurface as="form" onSubmit={handleSubmit}>
        <ModalHeader>
          <ModalTitle>{t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.title')}</ModalTitle>
          <ModalCloseButton
            type="button"
            onClick={closeModal(setShowModal)}
            aria-label={t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.closeAriaLabel')}
          >
            <Close.CloseSVG />
          </ModalCloseButton>
        </ModalHeader>
        <ModalNotice>{t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.notice')}</ModalNotice>
        <ModalFieldStack>
          <ModalLabel>
            {t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.meetingType')}
            <ModalSelect value={meetingType} onChange={(event) => setMeetingType(event.target.value as 'video' | 'voice')}>
              <option value="video">{t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.video')}</option>
              <option value="voice">{t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.voice')}</option>
            </ModalSelect>
          </ModalLabel>
          {options.map((option, index) => (
            <ModalFieldStack key={index}>
              <ModalLabel>
                {t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.optionStart', { number: index + 1 })}
                <ModalInput
                  type="datetime-local"
                  value={option.starts_at}
                  onChange={(event) => updateOption(index, 'starts_at', event.target.value)}
                />
              </ModalLabel>
              <ModalLabel>
                {t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.optionEnd', { number: index + 1 })}
                <ModalInput
                  type="text"
                  value={getPblMeetingEndDateTime(option.starts_at)?.replace('T', ' ') || ''}
                  readOnly
                />
              </ModalLabel>
              {option.starts_at && !isPblMeetingWithinSameDay(option.starts_at) && (
                <Text color={theme.colors.error} role="alert">{t('pbl.common.meetingEndsSameDay')}</Text>
              )}
              <ModalLabel>
                {t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.optionNote', { number: index + 1 })}
                <ModalInput value={option.note} onChange={(event) => updateOption(index, 'note', event.target.value)} />
              </ModalLabel>
              {canRemoveOption && (
                <Button type="button" size="md" variant="primary-invert" rounded={false} onClick={() => removeOption(index)}>
                  {t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.removeOption', { number: index + 1 })}
                </Button>
              )}
            </ModalFieldStack>
          ))}
          {canAddOption && (
            <Button type="button" size="md" variant="primary-invert" rounded={false} onClick={addOption}>
              {t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.addOption')}
            </Button>
          )}
          <ModalLabel>
            {t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.reason')}
            <ModalTextarea required={reasonRequired} value={reason} onChange={(event) => setReason(event.target.value)} />
          </ModalLabel>
        </ModalFieldStack>
        <ModalActions>
          <Button type="submit" size="lg" variant="primary" rounded={false} disabled={!canSubmit}>
            {isSubmitting
              ? t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.saving')
              : t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.save')}
          </Button>
          <Button type="button" size="lg" variant="primary-invert" rounded={false} onClick={closeModal(setShowModal)}>
            {t('pbl.caseDetailDocuments.modals.appointmentProposalEdit.cancel')}
          </Button>
        </ModalActions>
      </ModalSurface>
    </Modal>
  );
};

export const MeetingEditModal = ({
  showModal,
  setShowModal,
  meeting,
  isSubmitting,
  onSubmit,
}: BaseModalProps & {
  meeting: PblMeeting | null;
  isSubmitting: boolean;
  onSubmit: (payload: PblMeetingUpdatePayload) => Promise<void>;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const [meetingType, setMeetingType] = useState<'video' | 'voice'>('video');
  const [scheduledAt, setScheduledAt] = useState('');
  const [reason, setReason] = useState('');
  const modalTitle = getPblMeetingEditModalTitle(meeting?.purpose, t);
  const modalNotice = getPblMeetingEditModalNotice(meeting?.purpose, t);

  useEffect(() => {
    if (!showModal || !meeting) return;

    setMeetingType(meeting.meeting_type === 'voice' ? 'voice' : 'video');
    setScheduledAt(toDateTimeLocalValue(meeting.scheduled_at_display || meeting.scheduled_at));
    setReason('');
  }, [meeting, showModal]);

  const endsAt = getPblMeetingEndDateTime(scheduledAt);
  const canSubmit = Boolean(isPblMeetingWithinSameDay(scheduledAt) && !isSubmitting);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    await onSubmit({
      meeting_type: meetingType,
      scheduled_at: fromDateTimeLocalValue(scheduledAt),
      ends_at: fromDateTimeLocalValue(endsAt!),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    });
  };

  return (
    <Modal open={showModal} onClose={closeModal(setShowModal)} disableEnforceFocus>
      <ModalSurface as="form" onSubmit={handleSubmit}>
        <ModalHeader>
          <ModalTitle>{modalTitle}</ModalTitle>
          <ModalCloseButton
            type="button"
            onClick={closeModal(setShowModal)}
            aria-label={t('pbl.caseDetailDocuments.modals.meetingEdit.closeAriaLabel')}
          >
            <Close.CloseSVG />
          </ModalCloseButton>
        </ModalHeader>
        <ModalNotice>
          {modalNotice} {t('pbl.caseDetailDocuments.modals.meetingEdit.bbbNotice')}
        </ModalNotice>
        <ModalFieldStack>
          <ModalLabel>
            {t('pbl.caseDetailDocuments.modals.meetingEdit.meetingType')}
            <ModalSelect value={meetingType} onChange={(event) => setMeetingType(event.target.value as 'video' | 'voice')}>
              <option value="video">{t('pbl.caseDetailDocuments.modals.meetingEdit.video')}</option>
              <option value="voice">{t('pbl.caseDetailDocuments.modals.meetingEdit.voice')}</option>
            </ModalSelect>
          </ModalLabel>
          <ModalLabel>
            {t('pbl.caseDetailDocuments.modals.meetingEdit.scheduledStartTime')}
            <ModalInput type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} />
          </ModalLabel>
          <ModalLabel>
            {t('pbl.caseDetailDocuments.modals.meetingEdit.scheduledEndTime')}
            <ModalInput type="text" value={endsAt?.replace('T', ' ') || ''} readOnly />
          </ModalLabel>
          {scheduledAt && !isPblMeetingWithinSameDay(scheduledAt) && (
            <Text color={theme.colors.error} role="alert">{t('pbl.common.meetingEndsSameDay')}</Text>
          )}
          <ModalLabel>
            {t('pbl.caseDetailDocuments.modals.meetingEdit.reason')}
            <ModalTextarea value={reason} onChange={(event) => setReason(event.target.value)} />
          </ModalLabel>
        </ModalFieldStack>
        <ModalActions>
          <Button type="submit" size="lg" variant="primary" rounded={false} disabled={!canSubmit}>
            {isSubmitting
              ? t('pbl.caseDetailDocuments.modals.meetingEdit.saving')
              : t('pbl.caseDetailDocuments.modals.meetingEdit.save')}
          </Button>
          <Button type="button" size="lg" variant="primary-invert" rounded={false} onClick={closeModal(setShowModal)}>
            {t('pbl.caseDetailDocuments.modals.meetingEdit.cancel')}
          </Button>
        </ModalActions>
      </ModalSurface>
    </Modal>
  );
};

export const CloseCaseConfirmModal = ({
  showModal,
  setShowModal,
  isSubmitting,
  onConfirm,
  requireReason = false,
}: BaseModalProps & {
  isSubmitting: boolean;
  requireReason?: boolean;
  onConfirm: (reason?: string) => Promise<void>;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const [closureReason, setClosureReason] = useState('');
  useEffect(() => { if (!showModal) setClosureReason(''); }, [showModal]);

  return (
  <Modal open={showModal} onClose={closeModal(setShowModal)} disableEnforceFocus>
    <ModalSurface>
      <ModalHeader>
        <ModalTitle>{t('pbl.caseDetailDocuments.modals.closeCase.title')}</ModalTitle>
        <ModalCloseButton
          type="button"
          onClick={closeModal(setShowModal)}
          aria-label={t('pbl.caseDetailDocuments.modals.closeCase.closeAriaLabel')}
        >
          <Close.CloseSVG />
        </ModalCloseButton>
      </ModalHeader>
      <ModalNotice>{t(requireReason ? 'pbl.backlogContinuation.earlyClosureNotice' : 'pbl.caseDetailDocuments.modals.closeCase.notice')}</ModalNotice>
      {requireReason && <label>
        {t('pbl.backlogContinuation.closureReason')}
        <textarea aria-label={t('pbl.backlogContinuation.closureReason')} required maxLength={2000}
          disabled={isSubmitting} value={closureReason} onChange={(event) => setClosureReason(event.target.value)} />
      </label>}
      <ModalActions>
        <Button type="button" size="lg" variant="danger" rounded={false} disabled={isSubmitting || (requireReason && !closureReason.trim())} onClick={() => onConfirm(requireReason ? closureReason.trim() : undefined)}>
          {isSubmitting
            ? t('pbl.caseDetailDocuments.modals.closeCase.closing')
            : t('pbl.caseDetailDocuments.modals.closeCase.confirmClose')}
        </Button>
        <Button
          type="button"
          size="lg"
          variant="primary-invert"
          rounded={false}
          disabled={isSubmitting}
          onClick={closeModal(setShowModal)}
        >
          {t('pbl.caseDetailDocuments.modals.closeCase.cancel')}
        </Button>
      </ModalActions>
    </ModalSurface>
  </Modal>
  );
};

export const CancelCaseConfirmModal = ({
  showModal,
  setShowModal,
  isSubmitting,
  onConfirm,
}: BaseModalProps & {
  isSubmitting: boolean;
  onConfirm: (data: CancelCaseInput) => Promise<void>;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const trimmedReason = reason.trim();

  useEffect(() => {
    if (!showModal) {
      setReason('');
    }
  }, [showModal]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!trimmedReason || isSubmitting) return;

    await onConfirm({
      reason: trimmedReason,
    });
  };

  return (
    <Modal open={showModal} onClose={closeModal(setShowModal)} disableEnforceFocus>
      <ModalSurface as="form" onSubmit={handleSubmit}>
        <ModalHeader>
          <ModalTitle>{t('pbl.caseDetailDocuments.modals.cancelCase.title')}</ModalTitle>
          <ModalCloseButton
            type="button"
            onClick={closeModal(setShowModal)}
            aria-label={t('pbl.caseDetailDocuments.modals.cancelCase.closeAriaLabel')}
          >
            <Close.CloseSVG />
          </ModalCloseButton>
        </ModalHeader>
        <ModalNotice>{t('pbl.caseDetailDocuments.modals.cancelCase.notice')}</ModalNotice>
        <ModalFieldStack>
          <ModalLabel>
            {t('pbl.caseDetailDocuments.modals.cancelCase.reason')}
            <ModalTextarea value={reason} onChange={(event) => setReason(event.target.value)} />
          </ModalLabel>
        </ModalFieldStack>
        <ModalActions>
          <Button type="submit" size="lg" variant="danger" rounded={false} disabled={!trimmedReason || isSubmitting}>
            {isSubmitting
              ? t('pbl.caseDetailDocuments.modals.cancelCase.cancelling')
              : t('pbl.caseDetailDocuments.modals.cancelCase.confirmCancel')}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="primary-invert"
            rounded={false}
            disabled={isSubmitting}
            onClick={closeModal(setShowModal)}
          >
            {t('pbl.caseDetailDocuments.modals.cancelCase.cancel')}
          </Button>
        </ModalActions>
      </ModalSurface>
    </Modal>
  );
};

export const ReassignLawyerModal = ({
  showModal,
  setShowModal,
  isSubmitting,
  onConfirm,
}: BaseModalProps & {
  isSubmitting: boolean;
  onConfirm: (data: ReassignLawyerInput) => Promise<void>;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const trimmedReason = reason.trim();

  useEffect(() => {
    if (!showModal) {
      setReason('');
    }
  }, [showModal]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!trimmedReason || isSubmitting) return;

    await onConfirm({
      reason: trimmedReason,
    });
  };

  return (
    <Modal open={showModal} onClose={closeModal(setShowModal)} disableEnforceFocus>
      <ModalSurface as="form" onSubmit={handleSubmit}>
        <ModalHeader>
          <ModalTitle>{t('pbl.caseDetailDocuments.modals.reassignLawyer.title')}</ModalTitle>
          <ModalCloseButton
            type="button"
            onClick={closeModal(setShowModal)}
            aria-label={t('pbl.caseDetailDocuments.modals.reassignLawyer.closeAriaLabel')}
          >
            <Close.CloseSVG />
          </ModalCloseButton>
        </ModalHeader>
        <ModalNotice>{t('pbl.caseDetailDocuments.modals.reassignLawyer.notice')}</ModalNotice>
        <ModalFieldStack>
          <ModalLabel>
            {t('pbl.caseDetailDocuments.modals.reassignLawyer.reason')}
            <ModalTextarea value={reason} onChange={(event) => setReason(event.target.value)} />
          </ModalLabel>
        </ModalFieldStack>
        <ModalActions>
          <Button type="submit" size="lg" variant="primary" rounded={false} disabled={!trimmedReason || isSubmitting}>
            {isSubmitting
              ? t('pbl.caseDetailDocuments.modals.reassignLawyer.reassigning')
              : t('pbl.caseDetailDocuments.modals.reassignLawyer.confirmReassign')}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="primary-invert"
            rounded={false}
            disabled={isSubmitting}
            onClick={closeModal(setShowModal)}
          >
            {t('pbl.caseDetailDocuments.modals.reassignLawyer.cancel')}
          </Button>
        </ModalActions>
      </ModalSurface>
    </Modal>
  );
};

export const LawyerExitRequestModal = ({
  showModal,
  setShowModal,
  isSubmitting,
  onConfirm,
}: BaseModalProps & {
  isSubmitting: boolean;
  onConfirm: (data: LawyerExitRequestInput) => Promise<void>;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const [reasonCode, setReasonCode] = useState<LawyerExitRequestInput['reasonCode']>('scheduling_unavailable');
  const [reason, setReason] = useState('');
  const trimmedReason = reason.trim();

  useEffect(() => {
    if (!showModal) {
      setReasonCode('scheduling_unavailable');
      setReason('');
    }
  }, [showModal]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!trimmedReason || isSubmitting) return;

    await onConfirm({
      reasonCode,
      reason: trimmedReason,
    });
  };

  return (
    <Modal open={showModal} onClose={closeModal(setShowModal)} disableEnforceFocus>
      <ModalSurface as="form" onSubmit={handleSubmit}>
        <ModalHeader>
          <ModalTitle>{t('pbl.caseDetailDocuments.modals.lawyerExitRequest.title')}</ModalTitle>
          <ModalCloseButton
            type="button"
            onClick={closeModal(setShowModal)}
            aria-label={t('pbl.caseDetailDocuments.modals.lawyerExitRequest.closeAriaLabel')}
          >
            <Close.CloseSVG />
          </ModalCloseButton>
        </ModalHeader>
        <ModalNotice>{t('pbl.caseDetailDocuments.modals.lawyerExitRequest.notice')}</ModalNotice>
        <ModalFieldStack>
          <ModalLabel>
            {t('pbl.caseDetailDocuments.modals.lawyerExitRequest.reasonCode')}
            <ModalSelect
              value={reasonCode}
              onChange={(event) => setReasonCode(event.target.value as LawyerExitRequestInput['reasonCode'])}
            >
              <option value="scheduling_unavailable">
                {t('pbl.caseDetailDocuments.modals.lawyerExitRequest.reasonCodes.schedulingUnavailable')}
              </option>
              <option value="outside_expertise">
                {t('pbl.caseDetailDocuments.modals.lawyerExitRequest.reasonCodes.outsideExpertise')}
              </option>
              <option value="capacity_or_conflict">
                {t('pbl.caseDetailDocuments.modals.lawyerExitRequest.reasonCodes.capacityOrConflict')}
              </option>
              <option value="other">{t('pbl.caseDetailDocuments.modals.lawyerExitRequest.reasonCodes.other')}</option>
            </ModalSelect>
          </ModalLabel>
          <ModalLabel>
            {t('pbl.caseDetailDocuments.modals.lawyerExitRequest.reason')}
            <ModalTextarea value={reason} onChange={(event) => setReason(event.target.value)} />
          </ModalLabel>
        </ModalFieldStack>
        <ModalActions>
          <Button type="submit" size="lg" variant="primary" rounded={false} disabled={!trimmedReason || isSubmitting}>
            {isSubmitting
              ? t('pbl.caseDetailDocuments.modals.lawyerExitRequest.submitting')
              : t('pbl.caseDetailDocuments.modals.lawyerExitRequest.submitRequest')}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="primary-invert"
            rounded={false}
            disabled={isSubmitting}
            onClick={closeModal(setShowModal)}
          >
            {t('pbl.caseDetailDocuments.modals.lawyerExitRequest.cancel')}
          </Button>
        </ModalActions>
      </ModalSurface>
    </Modal>
  );
};

export const UnavailableActionModal = ({
  showModal,
  setShowModal,
  title,
  message,
}: BaseModalProps & {
  title: string;
  message: string;
}) => (
  <UnavailableActionModalContent showModal={showModal} setShowModal={setShowModal} title={title} message={message} />
);

const UnavailableActionModalContent = ({
  showModal,
  setShowModal,
  title,
  message,
}: BaseModalProps & {
  title: string;
  message: string;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();

  return (
  <Modal open={showModal} onClose={closeModal(setShowModal)} disableEnforceFocus>
    <ModalSurface>
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
        <ModalCloseButton
          type="button"
          onClick={closeModal(setShowModal)}
          aria-label={t('pbl.caseDetailDocuments.modals.unavailableAction.closeAriaLabel')}
        >
          <Close.CloseSVG />
        </ModalCloseButton>
      </ModalHeader>
      <ModalNotice>{message}</ModalNotice>
      <ModalActions>
        <Button type="button" size="lg" variant="primary" rounded={false} onClick={closeModal(setShowModal)}>
          {t('pbl.caseDetailDocuments.modals.unavailableAction.close')}
        </Button>
      </ModalActions>
    </ModalSurface>
  </Modal>
  );
};
